import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { makeStyles } from 'tss-react/mui';

import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { Avatar, Box, Button, Chip, IconButton, Typography } from '@mui/material';

import Utils from 'utils/Utils';

const useStyles = makeStyles()((theme) => ({
    related: {
        marginBottom: 30,
        fontSize: 36,
        fontWeight: 800
    },
    tag: {
        marginRight: 8,
        marginBottom: 8,
        textTransform: 'capitalize',
        '&.MuiChip-deleteIcon': {
            color: '#979797'
        },
        '&.MuiChip-colorPrimary .MuiChip-label': {
            fontWeight: 600,
            color: '#fff'
        }
    },
    tagAvatar: {
        '&&': {
            background: 'transparent',
            fontSize: 14,
            fontWeight: 400,
            width: 22,
            height: 22
        }
    },
    group: {
        background: '#F5F5F5',
        borderRadius: 28,
        padding: 20,
        marginBottom: 20
    },
    title: {
        textTransform: 'capitalize',
        fontWeight: 600
    },
    subtitle: {
        color: '#979797',
        fontSize: 12,
        marginTop: 4,
        textTransform: 'capitalize'
    },
    avatar: {
        background: '#fff',
        cursor: 'pointer'
    },
    list: {
        marginTop: 20,
        borderRadius: 18,
        background: '#FAFAFA'
    },
    listItem: {
        display: 'flex',
        alignItems: 'center',
        padding: 20,
        borderBottom: '2px solid #F5F5F5'
    },
    listItemTitle: {
        cursor: 'pointer'
    },
    chip: {
        background: '#F5F5F5',
        borderRadius: 8,
        marginTop: 12,
        '& .MuiChip-label': {
            color: '#979797'
        }
    },
    icon: {
        border: '1px solid #EEEEEE',
        color: theme.palette.primary.main
    },
    btnMore: {
        boxShadow: 'none',
        borderRadius: 100,
        fontSize: 14,
        transition: 'all 0.2s ease-out'
    }
}));

const Group = ({ type, list, robotTypesStore }) => {
    const { classes } = useStyles();
    const history = useHistory();
    const [open, setOpen] = useState(false);

    const length = list.length;
    const isLong = length > 3;
    if (isLong && !open) {
        list = list.slice(0, 3);
    }

    const handleItemClick = (event, id) => {
        event.preventDefault();
        window.open(`/${type}/${id}`, '_blank');
    };

    return (
        <Box className={classes.group} key={type}>
            <Box display="flex" alignItems="center">
                <Avatar
                    src={Utils.getImage(type)}
                    className={classes.avatar}
                    onClick={() => history.push('/search?query=type:' + type)}
                />
                <Box flex={1} pl={2} pr={2}>
                    <div className={classes.title}>{robotTypesStore.get(type)}</div>
                    <div className={classes.subtitle}>{list[0].bulletinFamily}</div>
                </Box>
                {isLong && (
                    <Button
                        className={classes.btnMore}
                        color="primary"
                        variant="contained"
                        onClick={() => setOpen(!open)}
                        endIcon={<KeyboardArrowDown style={{ transform: open ? 'rotate(180deg)' : 'initial' }} />}
                    >
                        {length}
                    </Button>
                )}
            </Box>
            <Box className={classes.list}>
                {list.map((item, index) => (
                    <Box className={classes.listItem} key={index}>
                        <Box flex={1}>
                            <Typography
                                className={classes.listItemTitle}
                                variant="body2"
                                onClick={(e) => handleItemClick(e, item.id)}
                            >
                                {item.title}
                            </Typography>
                            <Chip className={classes.chip} label={Utils.formatDateT(item.published)} />
                        </Box>
                        <IconButton
                            className={classes.icon}
                            size="medium"
                            href={`/${type}/${item.id}`}
                            onClick={(event) => handleItemClick(event, item.id)}
                            target="_blank"
                        >
                            <KeyboardArrowRight />
                        </IconButton>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const Related = ({ references, robotTypesStore }) => {
    const { classes } = useStyles();
    const [tags, setTags] = useState({});
    const [filterTags, setFilterTags] = useState(new Set());

    useEffect(() => {
        setTags(
            Object.keys(references).reduce((acc, value) => {
                const tag = references[value][0].bulletinFamily;
                acc[tag] = (acc[tag] || 0) + references[value].length;
                return acc;
            }, {})
        );
    }, [references]);

    const handleAddFilter = (tag) => {
        const newSet = new Set(filterTags);
        newSet.has(tag) ? newSet.delete(tag) : newSet.add(tag);
        setFilterTags(newSet);
    };

    let referenceTypes = Object.keys(references);
    if (filterTags.size > 0) {
        referenceTypes = referenceTypes.filter((type) => filterTags.has(references[type][0].bulletinFamily));
    }

    return (
        <Box>
            <Typography className={classes.related} variant="h3" id="related">
                {'Related'}
            </Typography>

            <Box pb={4}>
                {Object.keys(tags).map((tag, index) => (
                    <Chip
                        key={index}
                        className={classes.tag}
                        color={filterTags.has(tag) ? 'primary' : 'default'}
                        label={tag}
                        variant={filterTags.has(tag) ? 'filled' : 'outlined'}
                        deleteIcon={<Avatar className={classes.tagAvatar}>{tags[tag]}</Avatar>}
                        onDelete={() => handleAddFilter(tag)}
                        onClick={() => handleAddFilter(tag)}
                    />
                ))}
            </Box>

            {referenceTypes.map((type) => (
                <Group key={type} type={type} list={references[type]} robotTypesStore={robotTypesStore} />
            ))}
        </Box>
    );
};

export default inject('robotTypesStore')(observer(Related));
